import React from 'react'
import { editIcon } from '../../../assets/assets'
import Signature from '../../Consent/components/Signature'
import moment from 'moment'
import { LABEL, QUESTION_TYPES } from 'utils/constants'
import { formatAndSanitizeHTML } from 'utils/misc'
import { resolveChoiceIdFromStoredAnswer } from '../utils/instrumentUtils'
import QuestionTitleImage from './QuestionTitleImage'

const stripHtmlForReview = (html) =>
  String(html ?? '')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;|&#160;/gi, ' ')
    .replace(/\u00a0/g, ' ')
    .trim()

const isHtmlSurveyLabelEmpty = (label) => {
  if (label == null || label === '') return true
  if (label === LABEL) return false
  return stripHtmlForReview(label).length === 0
}

const getSelectMultipleReviewText = (question = {}) => {
  const choices = question?.choices || {}
  return (question?.answer || [])
    .map((item) => {
      const choiceId = item?.choice_id || item?.id
      const fromChoice =
        choiceId && choices[choiceId]?.label ? choices[choiceId].label : ''
      return stripHtmlForReview(
        item?.label || item?.unit || fromChoice || item?.value || '',
      )
    })
    .filter(Boolean)
    .join(', ')
}

const ReviewQuestionItem = (props) => {
  const { question = {}, onEditQuestion, strings, hideEdit = false } = props
  const { type, imageSrc, id } = question

  const getReviewTitleHtml = () => {
    if (question.label === LABEL) {
      return moment(question?.last_updated).format('DD/MM/YYYY')
    }
    const { label, attributes } = question
    if (
      type === QUESTION_TYPES.horizontalSelectOne &&
      attributes?.has_review_screen_text &&
      attributes?.review_screen_text &&
      isHtmlSurveyLabelEmpty(label)
    ) {
      return formatAndSanitizeHTML(attributes.review_screen_text)
    }
    return label || ''
  }

  const getFormattedAnswer = () => {
    const answerValue = question?.answer?.[0]?.value
    const answerLabel = question?.answer?.[0]?.label
    const answerChoiceValue = question?.answer?.[0]?.choice_value

    if (type === 'date' || type === 'datetime' || type === 'time') {
      return answerValue?.split(',')?.[0]
    }
    if (
      type === QUESTION_TYPES.selectOne ||
      type === QUESTION_TYPES.horizontalSelectOne
    ) {
      return answerLabel || answerChoiceValue || ''
    }
    if (type === 'select_multiple') {
      return getSelectMultipleReviewText(question)
    }
    if (type === QUESTION_TYPES.imageCapture) {
      const answerValues = question?.answer
        ?.map((item) => item.value || item.file_name)
        .filter(Boolean)
      return answerValues?.join(', ')
    }
    if (type === 'acq7_q7') {
      const answerValues = question?.answer?.map(
        (item) => `${item.label.replaceAll('_', ' ')} -> ${item.value}`,
      )
      return answerValues?.join('<br />')
    }
    if (type === QUESTION_TYPES.matrix) {
      const rows = question?.answer || []
      if (!rows.length) return ''
      const rowBlocks = rows
        .map((row) => {
          const choiceLabels = (row.answer || [])
            .map((a) => a?.label)
            .filter(Boolean)
          const choiceText = choiceLabels.join(', ')
          if (!row.label && !choiceText) return ''
          if (!choiceText) {
            return `<div class="review-matrix-answer-row"><div class="review-matrix-row-label">${row.label}</div></div>`
          }
          if (!row.label) {
            return `<div class="review-matrix-answer-row"><div class="review-matrix-row-choices">${choiceText}</div></div>`
          }
          return `<div class="review-matrix-answer-row"><div class="review-matrix-row-label">${row.label}</div><div class="review-matrix-row-choices">${choiceText}</div></div>`
        })
        .filter(Boolean)
      return `<div class="review-matrix-answer">${rowBlocks.join('')}</div>`
    }
    if (type === QUESTION_TYPES.timedMatchingGrid) {
      // GOLD-12178: TMG has no human-readable review answer (sequence
      // arrays). Leave the body empty; the row is also hidden unless
      // in_review is explicitly true.
      return ''
    }
    return answerValue
  }

  const formattedAnswer = getFormattedAnswer()

  return (
    <div className='review-question-box'>
      <div className='review-question-title'>
        <div className='review-question-title-main'>
          <QuestionTitleImage
            imageSrc={imageSrc}
            questionId={id}
            className='review-question-title-image'
          />
          <div
            className='review-question-title-text'
            dangerouslySetInnerHTML={{ __html: getReviewTitleHtml() }}
          />
        </div>
        {/* GOLD-12178: A timed sitting cannot be re-taken from Review. Hide the edit pencil even if the row is shown. */}
        {!hideEdit && type !== QUESTION_TYPES.timedMatchingGrid ? (
          <div
            className='review-edit-icon'
            onClick={() => onEditQuestion(question?.id)}>
            {question.label !== LABEL ? editIcon() : null}
          </div>
        ) : null}
      </div>
      {type === 'scribble' ? (
        <Signature
          strings={strings}
          signature={question?.answer?.[0]?.value}
          defaultSignaturePadRatio={0.25}
          readOnly
        />
      ) : type === 'select_multiple' ? (
        <div className='review-question-answer'>
          {formattedAnswer == null ? '' : String(formattedAnswer)}
        </div>
      ) : (
        <div
          className='review-question-answer'
          dangerouslySetInnerHTML={{
            __html: formattedAnswer == null ? '' : String(formattedAnswer),
          }}
        />
      )}
    </div>
  )
}

export default ReviewQuestionItem
